import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { PanelLeftClose, PanelLeftOpen, LogOut, ChevronRight, ChevronDown, ChevronUp, Search } from 'lucide-react';
import { AiOutlineStock } from 'react-icons/ai';
import feedaLogo from '../../assets/feedaia-logo.svg';
import logoIcon from '../../assets/logo-icon.svg';
import { NavData, SecondaryNavData } from '../Constant';
import SessionsListSection from './SessionsListSection';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../../Context/authContext';
import agentService from '../../services/agentService';
import { useQuery } from '@tanstack/react-query';
import { StyledSideBar, AgentMenuPanel } from './SideBar.styles';
import { MobileOverlay } from '../Layout/Layout.styles';

const secondaryIcons = {
  analytics: AiOutlineStock,
};

const getInitialCollapsed = () =>
  typeof window !== 'undefined' ? window.matchMedia('(max-width: 1199px)').matches : false;

const SideBar = () => {
  const [collapsed, setCollapsed] = useState(getInitialCollapsed);
  const [agentData, setAgentData] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState({
    label: 'All',
    value: '',
  });
  const [agentMenuOpen, setAgentMenuOpen] = useState(false);
  const [agentSearch, setAgentSearch] = useState('');
  const [agentMenuPos, setAgentMenuPos] = useState({ top: 0, left: 0 });
  const agentMenuRef = useRef(null);
  const agentTriggerRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  const { user, onLogout } = useContextHook(AuthContext, v => ({
    user: v.user,
    onLogout: v.onLogout,
  }));

  const getAllAgentData = useQuery({
    queryKey: ['get-all-agents'],
    queryFn: () => agentService.getAllAgent(),
    staleTime: Infinity,
    gcTime: Infinity,
  });

  useEffect(() => {
    if (getAllAgentData.isSuccess) {
      setAgentData([
        { agent_name: 'All', id: null },
        ...(getAllAgentData?.data?.agents || []),
      ]);
    }
  }, [getAllAgentData.isSuccess, getAllAgentData?.data?.agents]);

  useEffect(() => {
    if (!collapsed) {
      document.body.classList.add('sidebar-active');
    } else {
      document.body.classList.remove('sidebar-active');
      setAgentMenuOpen(false);
      setAgentSearch('');
    }
  }, [collapsed]);

  useEffect(() => {
    if (!agentMenuOpen) return undefined;
    const handleClick = e => {
      if (agentMenuRef.current && !agentMenuRef.current.contains(e.target)) {
        setAgentMenuOpen(false);
        setAgentSearch('');
      }
    };
    const handleKey = e => {
      if (e.key === 'Escape') {
        setAgentMenuOpen(false);
        setAgentSearch('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [agentMenuOpen]);

  const filteredAgents = useMemo(() => {
    const q = agentSearch.trim().toLowerCase();
    if (!q) return agentData;
    return agentData.filter(a => a.agent_name?.toLowerCase().includes(q));
  }, [agentData, agentSearch]);

  const isActive = item => {
    const path = `/${item.path}`;
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const initial = user?.first_name?.trim()?.[0]?.toUpperCase() || 'U';
  const displayName = user?.first_name || 'User';

  return (
    <>
      {!collapsed && <MobileOverlay type="button" aria-label="Close sidebar" onClick={() => setCollapsed(true)} />}
      <StyledSideBar $collapsed={collapsed}>
      <div className="nav-header">
        <Link to="/dashboard" className="logo-link" title="Gramie Labs">
          {collapsed ? (
            <img className="logo-sm" src={logoIcon} alt="Gramie Labs" width="24" height="30" />
          ) : (
            <img className="logo-lg" src={feedaLogo} alt="Gramie Labs" width="148" />
          )}
        </Link>
        <button
          type="button"
          className="collapse-btn"
          onClick={() => setCollapsed(c => !c)}
          title={collapsed ? 'Expand' : 'Collapse'}>
          {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
        </button>
      </div>

      <nav className="scroll-area">
        <ul className="nav-links">
          {NavData.map(elem => {
            const isChatNav = elem.path === 'dashboard';
            const onChatRoute = location.pathname === '/chat' || location.pathname.startsWith('/chat/');
            return (
              <li key={elem.path}>
                <NavLink
                  to={`/${elem.path}`}
                  title={collapsed ? elem.name : undefined}
                  className={({ isActive: active }) => (active || (isChatNav && onChatRoute) ? 'active' : undefined)}>
                  <img src={elem?.img} alt="" className="icon" />
                  {!collapsed && <span className="label">{elem?.name}</span>}
                </NavLink>
              </li>
            );
          })}
        </ul>

        {!collapsed && (
          <div className="chat-history">
            <div className="head">
              <span className="subheading">Chats</span>
              <div className="agent-filter" ref={agentMenuRef}>
                <button
                  ref={agentTriggerRef}
                  type="button"
                  className="agent-trigger"
                  onClick={() => {
                    const next = !agentMenuOpen;
                    if (next && agentTriggerRef.current) {
                      const rect = agentTriggerRef.current.getBoundingClientRect();
                      setAgentMenuPos({ top: rect.top, left: rect.right + 8 });
                    }
                    setAgentMenuOpen(next);
                  }}>
                  <span className="agent-name">{selectedAgent?.label || 'All'}</span>
                  {agentMenuOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                </button>
                {agentMenuOpen && (
                  <AgentMenuPanel style={{ top: agentMenuPos.top, left: agentMenuPos.left }}>
                    <div className="search-wrap">
                      <Search className="search-icon" size={14} />
                      <input
                        autoFocus
                        value={agentSearch}
                        onChange={e => setAgentSearch(e.target.value)}
                        placeholder="Search agents..."
                      />
                    </div>
                    <div className="agent-list">
                      {filteredAgents.length === 0 ? (
                        <p className="empty">No agents found.</p>
                      ) : (
                        filteredAgents.map(data => {
                          const selected = (selectedAgent?.value || null) === (data?.id || null);
                          return (
                            <button
                              key={data?.id ?? 'all'}
                              type="button"
                              className={selected ? 'selected' : undefined}
                              onClick={() => {
                                setSelectedAgent({ label: data?.agent_name, value: data?.id });
                                setAgentMenuOpen(false);
                                setAgentSearch('');
                              }}>
                              {data?.agent_name}
                            </button>
                          );
                        })
                      )}
                    </div>
                  </AgentMenuPanel>
                )}
              </div>
            </div>
            <div className="sessions-wrap">
              <SessionsListSection selectedAgent={selectedAgent} />
            </div>
          </div>
        )}

        <div className="sidebar-footer">
          <div className="secondary-nav">
            <ul className="nav-links">
              {SecondaryNavData.map(item => {
                const Icon = secondaryIcons[item.path] || AiOutlineStock;
                const active = isActive(item);
                return (
                  <li key={item.path}>
                    <NavLink
                      to={`/${item.path}`}
                      title={collapsed ? item.name : undefined}
                      className={active ? 'active' : undefined}>
                      <Icon className="icon" size={22} />
                      {!collapsed && <span className="label">{item.name}</span>}
                    </NavLink>
                  </li>
                );
              })}
              <li>
                <button
                  type="button"
                  className="btn-logout"
                  onClick={onLogout}
                  title={collapsed ? 'Logout' : undefined}>
                  <LogOut className="icon" size={20} />
                  {!collapsed && <span className="label">Logout</span>}
                </button>
              </li>
            </ul>
          </div>

          <div
            className="profile-holder"
            onClick={() => navigate('/settings')}
            onKeyDown={e => e.key === 'Enter' && navigate('/settings')}
            role="button"
            tabIndex={0}>
            <div className="profile-img">
              <span className="text">{initial}</span>
            </div>
            {!collapsed && (
              <>
                <div className="profile-info">
                  <span className="name">{displayName}</span>
                  <span className="text">Free plan</span>
                </div>
                <ChevronRight className="icon-arrow" size={18} />
              </>
            )}
          </div>
        </div>
      </nav>
    </StyledSideBar>
    </>
  );
};

export default SideBar;
